import { defineStore } from "pinia";
import axios from "axios";

// Ottieni l'URL dall'ambiente
const API_URL = process.env.VUE_APP_API_URL;

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null, // Informazioni sull'utente loggato
    token: null, // Token di autenticazione
    isLoggedIn: false, // Stato di autenticazione
    errorMessage: "", // Messaggio di errore durante il login
    sessionExpiry: null, // Timestamp di scadenza della sessione
    inactivityTimeout: null, // Timer per l'inattività
  }),
  actions: {
    // Effettua il login
    async login(loginData) {
      try {
        const response = await axios.post(
          `${API_URL}/api/auth/login`,
          {
            username: loginData.username,
            password: loginData.password,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        this.user = {
          name: response.data.username,
          admin: response.data.admin,
          denominazione: response.data.denominazione,
          indirizzo: response.data.indirizzo,
          provAbilitato: response.data.prvList,
          listaProvince: response.data.listaProvince,
          listaComuni: response.data.listaComuni,
          atecoList: response.data.atecoList,
        };
        this.token = response.data.accessToken;
        this.isLoggedIn = true;
        this.errorMessage = "";

        // Calcola il timestamp di scadenza (esempio: 1 ora)
        const expiryTime = Date.now() + 60 * 60 * 1000; // 1 ora in millisecondi
        this.sessionExpiry = expiryTime;

        // Salva i dati della sessione nel sessionStorage
        sessionStorage.setItem("token", this.token);
        sessionStorage.setItem("user", JSON.stringify(this.user));
        sessionStorage.setItem("sessionExpiry", expiryTime);

        // Avvia il timer di inattività
        this.startInactivityTimer();
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message || "Errore durante il login";
        throw error;
      }
    },

    // Effettua il logout
    logout(clearMapCallback) {
      this.user = null;
      this.token = null;
      this.isLoggedIn = false;
      this.sessionExpiry = null;

      // Rimuovi i dati della sessione dal sessionStorage
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("sessionExpiry");

      // Cancella il timer di inattività
      this.clearInactivityTimer();

      // Richiama il metodo clearMap passato come callback
      if (typeof clearMapCallback === "function") {
        clearMapCallback();
      }
    },

    // Verifica lo stato di autenticazione
    checkLoginStatus() {
      const token = sessionStorage.getItem("token");
      const userData = sessionStorage.getItem("user");
      const sessionExpiry = sessionStorage.getItem("sessionExpiry");

      if (token && sessionExpiry && Date.now() < parseInt(sessionExpiry, 10)) {
        this.token = token;
        this.isLoggedIn = true;
        this.sessionExpiry = parseInt(sessionExpiry, 10);

        if (userData && userData !== "undefined") {
          try {
            this.user = JSON.parse(userData);
          } catch (error) {
            console.error("Errore durante il parsing dei dati utente:", error);
            this.user = null;
          }
        } else {
          this.user = null;
        }

        // Riavvia il timer di inattività
        this.startInactivityTimer();
      } else {
        this.logout(); // Disconnetti l'utente se il token è scaduto
      }
    },

    startInactivityTimer() {
      // Esegui il controllo solo se l'utente è loggato
      if (!this.isLoggedIn) {
        return;
      }
      // Cancella eventuali timer esistenti
      this.clearInactivityTimer();

      // Imposta un nuovo timer di inattività (esempio: 15 minuti)
      this.inactivityTimeout = setTimeout(() => {
        console.warn("Sessione scaduta per inattività.");
        this.logout(); // Effettua il logout per inattività
      }, 15 * 60 * 1000); // 15 minuti in millisecondi
    },

    clearInactivityTimer() {
      if (this.inactivityTimeout) {
        clearTimeout(this.inactivityTimeout);
        this.inactivityTimeout = null;
      }
    },

    resetInactivityTimer() {
      if (!this.isLoggedIn) {
        //console.warn("Tentativo di resettare il timer di inattività senza essere loggati.");
        return;
      }

      // Aggiorna il timestamp di scadenza della sessione (esempio: 1 ora da ora)
      const newExpiryTime = Date.now() + 60 * 60 * 1000; // 1 ora in millisecondi
      this.sessionExpiry = newExpiryTime;

      // Aggiorna il valore nel sessionStorage
      sessionStorage.setItem("sessionExpiry", newExpiryTime);

      // Riavvia il timer di inattività
      this.startInactivityTimer();
    },
  },
});
